import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ISuperTableCalculation, ISuperTableCalculationFunction } from '@modeso/types__aumico-lib-annualreport-be';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { IXAxisConfigColumn, SuperTableColumnType } from '@modeso/types__aumico-lib-notes-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { ISuperTableAddCalculationResult } from '../../../../interfaces/super-table-add-calculation-result.interface';
import { ISuperTableItemSelection } from '../../../../interfaces/super-table-item-selection.interface';

@UntilDestroy()
@Component({
    selector: 'super-table-ratio-row',
    templateUrl: './super-table-ratio-row.component.html',
    styleUrls: ['./super-table-ratio-row.component.scss'],
})
export class SuperTableRatioRowComponent implements OnInit, OnChanges {
    @Input() rowSelectionOptions: ISuperTableItemSelection[] = [];
    @Input() columnSelectionOptions: IXAxisConfigColumn[] = [];
    @Input() existingCalculation: ISuperTableCalculation | undefined;
    @Input() language: string | undefined;
    selectedNumeratorItems: ISuperTableItemSelection[] = [];
    selectedDenomenatorItem: ISuperTableItemSelection | undefined;
    titleControl = new FormControl('');
    numeratorDropdownOpen = false;
    denomenatorDropdownOpen = false;
    nameIdColSelections: INameId[] = [];

    @Output() calculationOutput: EventEmitter<ISuperTableAddCalculationResult> =
        new EventEmitter<ISuperTableAddCalculationResult>();

    constructor(private localizedTexts: I18NextService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['columnSelectionOptions']?.currentValue) {
            this.prepareNameIdColSelections();
        }

        if (changes['existingCalculation']?.currentValue) {
            this.titleControl.setValue(this.existingCalculation.title);

            const selectedRows = this.existingCalculation.rowsIds;

            this.selectedNumeratorItems = this.rowSelectionOptions.filter((option) =>
                selectedRows.includes(option.name),
            );

            this.selectedDenomenatorItem = this.rowSelectionOptions.find(
                (option) => this.existingCalculation.relationItemId === option.name,
            );

            this.calculationOutput.emit({
                title: this.titleControl.value,
                function: ISuperTableCalculationFunction.RATIO,
                rowSelections: this.selectedNumeratorItems,
                columnSelections: this.nameIdColSelections, // add all columns
                relationItem: this.selectedDenomenatorItem,
            });
        }
    }

    ngOnInit(): void {
        this.titleControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
            this.calculationOutput.emit({
                title: this.titleControl.value,
                function: ISuperTableCalculationFunction.RATIO,
                rowSelections: this.selectedNumeratorItems,
                columnSelections: this.nameIdColSelections, // add all columns
                relationItem: this.selectedDenomenatorItem,
            });
        });
    }

    closeDropDowns(): void {
        this.numeratorDropdownOpen = false;
        this.denomenatorDropdownOpen = false;
    }

    toggleNumeratorDropdown(event: MouseEvent): void {
        event.stopPropagation();
        this.numeratorDropdownOpen = !this.numeratorDropdownOpen;
        if (this.numeratorDropdownOpen) {
            this.denomenatorDropdownOpen = false;
        }
    }

    toggleDenomenatorDropdown(event: MouseEvent): void {
        event.stopPropagation();
        this.denomenatorDropdownOpen = !this.denomenatorDropdownOpen;
        if (this.denomenatorDropdownOpen) {
            this.numeratorDropdownOpen = false;
        }
    }

    onSelectNumeratorItem(item: ISuperTableItemSelection): void {
        const index = this.selectedNumeratorItems.findIndex((selected) => selected.name === item.name);
        if (index === -1) {
            this.selectedNumeratorItems.push(item);
        } else {
            this.selectedNumeratorItems.splice(index, 1);
        }

        this.calculationOutput.emit({
            title: this.titleControl.value,
            function: ISuperTableCalculationFunction.RATIO,
            rowSelections: this.selectedNumeratorItems,
            columnSelections: this.nameIdColSelections, // add all columns
            relationItem: this.selectedDenomenatorItem,
        });
    }

    onSelectDenomenatorItem(item: ISuperTableItemSelection): void {
        if (this.selectedDenomenatorItem?.name === item.name) {
            //deselect denomenator
            this.selectedDenomenatorItem = null;
        } else {
            this.selectedDenomenatorItem = item;
        }

        this.calculationOutput.emit({
            title: this.titleControl.value,
            function: ISuperTableCalculationFunction.RATIO,
            rowSelections: this.selectedNumeratorItems,
            columnSelections: this.nameIdColSelections, // add all columns
            relationItem: this.selectedDenomenatorItem,
        });
    }

    removeNumeratorItem(item: ISuperTableItemSelection) {
        this.selectedNumeratorItems = this.selectedNumeratorItems.filter((selected) => selected.name !== item.name);

        this.calculationOutput.emit({
            title: this.titleControl.value,
            function: ISuperTableCalculationFunction.RATIO,
            rowSelections: this.selectedNumeratorItems,
            columnSelections: this.nameIdColSelections, // add all columns
            relationItem: this.selectedDenomenatorItem,
        });
    }

    removeDenomenatorItem() {
        this.selectedDenomenatorItem = null;

        this.calculationOutput.emit({
            title: this.titleControl.value,
            function: ISuperTableCalculationFunction.RATIO,
            rowSelections: this.selectedNumeratorItems,
            columnSelections: this.nameIdColSelections, // add all columns
            relationItem: this.selectedDenomenatorItem,
        });
    }

    isNumeratorSelected(item: ISuperTableItemSelection) {
        return this.selectedNumeratorItems.find((selected) => selected.name === item.name) != null;
    }

    private prepareNameIdColSelections(): void {
        for (const col of this.columnSelectionOptions) {
            if (col.type === SuperTableColumnType.TEXT) {
                continue;
            }
            if (col.type === SuperTableColumnType.CURRENT_YEAR) {
                this.nameIdColSelections.push({
                    id: col.columnId,
                    name: this.localizedTexts.t('aumico_notes_super_table_options_col_type_current_year') as string,
                });
                continue;
            }
            if (col.type === SuperTableColumnType.PREVIOUS_YEAR) {
                this.nameIdColSelections.push({
                    id: col.columnId,
                    name: this.localizedTexts.t('aumico_notes_super_table_options_col_type_previous_year') as string,
                });
                continue;
            }
            this.nameIdColSelections.push({
                id: col.columnId,
                name: col.title[this.language],
            });
        }
    }
}
